import { VideoExportProvider } from "@/animation/video-generator/components/VideoExportContext";
import { fetchTab } from "@/api/hooks/tab";
import { FloatingBoundaryProvider } from "@/components/context/FloatingBoundaryContext";
import { setTitle } from "@/effects";
import { Grid } from "@/grid";
import { GridCellHighlightProvider } from "@/grid/GridCellHighlightProvider";
import { MapDrawingMasterMachineProvider } from "@/models/map-drawing-tools/reactContext";
import { MapInteractionStateMachineProvider } from "@/models/map-interaction/mapInteractionStateContext";
import { TempLayerStateMachineProvider } from "@/models/temporal-layer/tempLayerStateMachineContext";
import { useDisplayTime } from "@/models/time-control/timeStateHooks";
import { ContextMenuProvider } from "@/overlay/components/ContextMenu";
import { DRAGGABLE_AREA_HEIGHT, useDesktop } from "@/overlay/components/window";
import { PrefetchController } from "@/prefetch-controller";
import reduxStore, { type RootState, useAppDispatch } from "@/reducer";
import { flushAutosaveQueue, tabAutoSaveListener } from "@/reducer/auto-save/subscription";
import { PopupPlotProvider } from "@/reducer/plot";
import { WorkspaceKeyShortcutProvider } from "@/utility/key-shortcuts/WorkspaceKeyShortcutProvider";
import { observeResizeWithAnimationFrame } from "@/utility/observeResizeWithAnimationFrame";
import { useSearchEngineReady } from "@/weather-parameters";
import { t } from "@lingui/core/macro";
import { useProfileAndTabParams } from "metx-core/src/hooks";
import { type ComponentType, Suspense, lazy, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import "./workspace.scss";

/**
 * A GUI overlay consists out of two parts
 * - the ViewportOverlay that is drawn on top of the whole browser viewport (across all maps and plots)
 * - the MapOverlay that is drawn on top of each map.
 *
 * The root of the overlay package is expected to export an object with both these properties. If one
 * of the overlay types is not needed, just return an empty component as follows: `export () => <></>`.
 *
 */
type OverlayModule = {
  ViewportOverlay: ComponentType<any>;
};
const DRAGGABLE_AREA_SYNC_SETTLE_DELAY_MS = 120;
const OVERLAY_NAME = "default";

// TODO: this file needs a massive cleanup to improve the loading screen
export default function WorkspacePage() {
  const { tabId } = useProfileAndTabParams();
  const dispatch = useAppDispatch();
  const isInteractionLocked = useSelector((state: RootState) => state.ui.isInteractionLocked);
  useEffect(() => {
    dispatch(fetchTab({ tabId }));
  }, [dispatch, tabId]);

  // in case of the workspace, the title is only shown until the overlay loads. the overlay should set the title afterwards.
  useEffect(() => {
    setTitle(t({ id: "workspace.title.whileLoading", message: "Workspace" }));

    const unsubscribe = reduxStore.subscribe(tabAutoSaveListener);

    return () => {
      unsubscribe();
      flushAutosaveQueue();
    };
  }, []);

  // TODO move overlay name to profile, previously it was on account, but always "default"
  const displayTime = useDisplayTime();

  // we arbitrarily assert here that the requests that were racing to fetch parameter and model info
  // have already loaded. This lets users login while this info is in-flight. On the other hand, the whole
  // <Grid> of `<Map>`s needs access to weather parameters. So, without placing this check here, the user
  // will end up with a rather blank page since both overlay and grid initialize lazily.
  const searchEngineReady = useSearchEngineReady();

  // TODO: clean error handling if import of overlay fails
  const { ViewportOverlay } = useMemo(() => {
    // Vite's dynamic-import analysis needs the file extension in the static
    // part of the pattern, so we point at the overlay's index module directly.
    const overlayModule: Promise<OverlayModule> = import(`@/overlay/overlays/${OVERLAY_NAME}/index.tsx`);
    const errorComponentModule = { default: () => <div>Loading overlay failed</div> };

    return {
      ViewportOverlay: lazy(() =>
        overlayModule.then(({ ViewportOverlay }) => ({ default: ViewportOverlay })).catch(() => errorComponentModule),
      ),
    };
  }, []);

  const overlayLayerRef = useRef<HTMLDivElement>(null);
  const resizeSettleTimeoutRef = useRef<number | null>(null);
  const lastBoundsRef = useRef<{ left: number; top: number; right: number; bottom: number } | null>(null);
  const { updateDraggableArea } = useDesktop();

  useEffect(() => {
    const target = overlayLayerRef.current;
    if (!target) {
      return undefined;
    }

    const applyBounds = () => {
      const rect = target.getBoundingClientRect();
      const next = {
        left: Math.round(rect.left),
        top: Math.round(rect.top),
        right: Math.round(rect.right),
        bottom: Math.round(rect.bottom),
      };
      const prev = lastBoundsRef.current;
      if (
        prev &&
        prev.left === next.left &&
        prev.top === next.top &&
        prev.right === next.right &&
        prev.bottom === next.bottom
      ) {
        return;
      }
      lastBoundsRef.current = next;
      updateDraggableArea({ ...next, top: DRAGGABLE_AREA_HEIGHT });
    };

    const scheduleApplyBounds = () => {
      if (resizeSettleTimeoutRef.current !== null) {
        window.clearTimeout(resizeSettleTimeoutRef.current);
      }

      resizeSettleTimeoutRef.current = window.setTimeout(() => {
        resizeSettleTimeoutRef.current = null;
        requestAnimationFrame(() => {
          applyBounds();
        });
      }, DRAGGABLE_AREA_SYNC_SETTLE_DELAY_MS);
    };

    const handleSidebarTransitionEnd = (event: Event) => {
      const transitionEvent = event as TransitionEvent;
      const sidebarElement = document.getElementById("app-sidebar");
      if (transitionEvent.target !== sidebarElement || transitionEvent.propertyName !== "width") {
        return;
      }

      if (resizeSettleTimeoutRef.current !== null) {
        window.clearTimeout(resizeSettleTimeoutRef.current);
        resizeSettleTimeoutRef.current = null;
      }

      requestAnimationFrame(() => {
        applyBounds();
      });
    };

    applyBounds();

    const stopObservingResize = observeResizeWithAnimationFrame(target, () => {
      scheduleApplyBounds();
    });
    const sidebarElement = document.getElementById("app-sidebar");

    sidebarElement?.addEventListener("transitionend", handleSidebarTransitionEnd);

    return () => {
      stopObservingResize();
      sidebarElement?.removeEventListener("transitionend", handleSidebarTransitionEnd);
      if (resizeSettleTimeoutRef.current !== null) {
        window.clearTimeout(resizeSettleTimeoutRef.current);
        resizeSettleTimeoutRef.current = null;
      }
    };
  }, [updateDraggableArea]);

  if (!searchEngineReady || displayTime == null) {
    return <mm-loader size="xlarge" />;
  }

  return (
    <div className="page page--workspace cover-window overlay">
      <VideoExportProvider>
        <PrefetchController />
        <GridCellHighlightProvider>
          <MapDrawingMasterMachineProvider>
            <MapInteractionStateMachineProvider>
              <TempLayerStateMachineProvider>
                <WorkspaceKeyShortcutProvider>
                  <PopupPlotProvider>
                    <FloatingBoundaryProvider value={overlayLayerRef}>
                      <div className="overlay__layer" ref={overlayLayerRef}>
                        <ContextMenuProvider>
                          <Grid tabId={tabId} />
                        </ContextMenuProvider>
                      </div>
                    </FloatingBoundaryProvider>
                    {isInteractionLocked && (
                      <div className="overlay__layer metx-ui-lock">
                        <div className="metx-ui-lock__content">
                          <mm-loader size="large" />
                          <div className="metx-ui-lock__text">Analysing workspace...</div>
                        </div>
                      </div>
                    )}
                    <div className="gui-overlay overlay__layer">
                      <Suspense>
                        <ViewportOverlay />
                      </Suspense>
                    </div>
                  </PopupPlotProvider>
                </WorkspaceKeyShortcutProvider>
              </TempLayerStateMachineProvider>
            </MapInteractionStateMachineProvider>
          </MapDrawingMasterMachineProvider>
        </GridCellHighlightProvider>
      </VideoExportProvider>
    </div>
  );
}
